/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState, } from "react";
import moment, { Moment, } from "moment";
import cn from "./helpers/cn";

type TimeInput = Date | Moment | string | null | undefined;

interface KeyboardTimePickerProps {
  label?: React.ReactNode;
  value?: TimeInput;
  onChange?: (date: Date | null, value?: string | null) => void;
  format?: string;
  /** 12h (am/pm) ou 24h. Default 12h, igual ao MUI v4. */
  ampm?: boolean;
  /** Incremento do minuto na seleção. Default 1. */
  minutesStep?: number;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  invalidDateMessage?: React.ReactNode;
  autoOk?: boolean;
  className?: string;
  style?: React.CSSProperties;
  color?: "primary" | "secondary";
  placeholder?: string;
  id?: string;
  name?: string;
  /** Ícone do botão lateral (default = relógio). */
  keyboardIcon?: React.ReactNode;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  InputLabelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  KeyboardButtonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
}

const toMoment = (value: TimeInput, format?: string): Moment | null => {
  if (value === null || value === undefined || value === "") return null;
  const m = typeof value === "string"
    ? moment(value, format, true).isValid() ? moment(value, format, true) : moment(value)
    : moment(value);

  return m.isValid() ? m : null;
};

const ClockIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const KeyboardTimePicker = React.forwardRef<HTMLInputElement, KeyboardTimePickerProps>((props, ref) => {
  const {
    label,
    value,
    onChange,
    format: formatProp,
    ampm = true,
    minutesStep = 1,
    fullWidth = false,
    disabled = false,
    required = false,
    error: errorProp = false,
    helperText,
    invalidDateMessage = "Invalid Time Format",
    autoOk = false,
    className,
    style,
    color = "primary",
    placeholder,
    id,
    name,
    keyboardIcon,
    onBlur,
    InputLabelProps,
    inputProps,
    KeyboardButtonProps,
  } = props;

  // Format default: 12h "hh:mm a" / 24h "HH:mm". Aceita os tokens do MUI/date-fns
  // ("HH"/"hh") já compatíveis com moment.
  const normalizedFormat = useMemo(
    () => formatProp ?? (ampm ? "hh:mm a" : "HH:mm"),
    [formatProp, ampm]
  );

  const selected = useMemo(() => toMoment(value, normalizedFormat), [value, normalizedFormat]);

  // Estado local do que está no input — pode estar incompleto/inválido enquanto digita.
  const [inputValue, setInputValue] = useState<string>(selected ? selected.format(normalizedFormat) : "");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(selected ? selected.format(normalizedFormat) : "");
    setInvalid(false);
  }, [selected, normalizedFormat]);

  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);

    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const emitChange = (m: Moment | null) => {
    if (!onChange) return;
    onChange(m ? m.toDate() : null, m ? m.format(normalizedFormat) : null);
  };

  // Máscara durante digitação. ampm true: "hh:mm aa" (8 chars + espaço); false: "HH:mm".
  const applyMask = (raw: string): string => {
    if (ampm) {
      // Aceita "hh:mm am/pm" — usa a parte de letras tal como o usuário digitar.
      const digits = raw.replace(/[^0-9]/g, "").slice(0, 4);
      const letters = raw.replace(/[^a-zA-Z]/g, "").slice(0, 2).toLowerCase();
      const parts = [digits.slice(0, 2), digits.slice(2, 4)].filter(Boolean);
      const time = parts.join(":");

      return letters ? `${time} ${letters}` : time;
    }
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    const parts = [digits.slice(0, 2), digits.slice(2, 4)].filter(Boolean);

    return parts.join(":");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyMask(e.target.value);

    setInputValue(masked);

    const parsed = moment(masked, normalizedFormat, true);

    if (parsed.isValid()) {
      setInvalid(false);
      emitChange(parsed);
    } else if (masked === "") {
      setInvalid(false);
      emitChange(null);
    } else {
      // Mantém inválido até o usuário concluir; commit acontece no blur.
      setInvalid(false);
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (inputValue) {
      const parsed = moment(inputValue, normalizedFormat, true);

      if (!parsed.isValid()) {
        setInvalid(true);
        emitChange(null);
      } else {
        setInvalid(false);
        emitChange(parsed);
      }
    } else {
      setInvalid(false);
    }
    if (onBlur) onBlur(e);
  };

  // Lista de horas e minutos para o popover.
  const hours = useMemo(() => {
    if (ampm) return Array.from({ length: 12 }, (_, i) => i === 0 ? 12 : i);

    return Array.from({ length: 24 }, (_, i) => i);
  }, [ampm]);

  const minutes = useMemo(
    () => Array.from({ length: Math.ceil(60 / minutesStep) }, (_, i) => i * minutesStep).filter((m) => m < 60),
    [minutesStep]
  );

  const selectedH = selected ? (ampm ? (selected.hours() % 12 || 12) : selected.hours()) : null;
  const selectedM = selected ? selected.minutes() : null;
  const selectedMeridiem: "am" | "pm" = selected ? (selected.hours() >= 12 ? "pm" : "am") : "am";

  const pickPart = (h?: number, m?: number, meridiem?: "am" | "pm") => {
    const base = selected ? selected.clone() : moment().startOf("day");
    const finalH = h !== undefined ? h : (selectedH ?? 0);
    const finalM = m !== undefined ? m : (selectedM ?? 0);
    const finalMeridiem = meridiem ?? selectedMeridiem;

    if (ampm) {
      let hour24 = finalH % 12;

      if (finalMeridiem === "pm") hour24 += 12;
      base.hours(hour24);
    } else {
      base.hours(finalH);
    }
    base.minutes(finalM);
    base.seconds(0);
    base.milliseconds(0);
    setInvalid(false);
    emitChange(base);
    if (autoOk) setOpen(false);
  };

  const hasError = errorProp || invalid;

  const borderClasses = hasError
    ? "border-red-600 ring-red-600"
    : focused
      ? color === "primary" ? "border-primary ring-primary ring-1" : "border-secondary ring-secondary ring-1"
      : "border-slate-300 hover:border-slate-900";

  const labelColor = hasError
    ? "text-red-600"
    : disabled
      ? "text-slate-400"
      : focused
        ? color === "primary" ? "text-primary" : "text-secondary"
        : "text-slate-500";

  // Mensagem auxiliar prioriza o invalidDateMessage quando o valor não é parseável.
  const displayedHelper = invalid ? invalidDateMessage : helperText;

  return (
    <div
      ref={containerRef}
      style={style}
      className={cn("inline-flex flex-col relative", fullWidth && "w-full", className)}
    >
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium mb-1.5 ml-1 transition-colors duration-200",
            labelColor
          )}
          {...InputLabelProps}
        >
          {label}
          {required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}

      <div className={cn(
        "relative flex items-center bg-white border rounded transition-all duration-200",
        borderClasses,
        disabled && "bg-slate-50 border-slate-200 cursor-not-allowed"
      )}>
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          inputMode={ampm ? "text" : "numeric"}
          autoComplete="off"
          disabled={disabled}
          required={required}
          value={inputValue}
          placeholder={placeholder ?? normalizedFormat.toLowerCase()}
          onFocus={() => setFocused(true)}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          className={cn(
            "w-full px-3 py-2.5 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 rounded",
            disabled && "text-slate-400 cursor-not-allowed"
          )}
          {...inputProps}
        />

        <button
          type="button"
          aria-label="change time"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          className={cn(
            "shrink-0 p-1.5 mr-1 rounded-full transition-colors",
            disabled ? "text-slate-300 cursor-not-allowed" : "text-slate-500 hover:bg-slate-100"
          )}
          {...KeyboardButtonProps}
        >
          {keyboardIcon ?? <ClockIcon />}
        </button>
      </div>

      {displayedHelper && (
        <p className={cn(
          "mt-1.5 text-xs ml-3.5",
          hasError ? "text-red-600" : "text-slate-500",
          disabled && "text-slate-400"
        )}>
          {displayedHelper}
        </p>
      )}

      {open && !disabled && (
        <div className="absolute z-50 top-full mt-2 right-0 bg-white border border-slate-200 rounded-lg shadow-xl p-3 w-60 animate-modal-in">
          <div className="flex gap-2">
            {/* Coluna de horas */}
            <div className="flex-1">
              <div className="text-[11px] text-slate-500 text-center font-medium py-1 mb-1 border-b border-slate-100">
                {ampm ? "h" : "HH"}
              </div>
              <div className="h-40 overflow-y-auto">
                {hours.map((h) => {
                  const isSel = selectedH === h;

                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => pickPart(h, undefined, undefined)}
                      className={cn(
                        "block w-full text-sm py-1 rounded text-center transition-colors",
                        isSel
                          ? color === "primary" ? "bg-primary text-contrast-text-primary" : "bg-secondary text-contrast-text-secondary"
                          : "text-slate-700 hover:bg-slate-100"
                      )}
                    >
                      {String(h).padStart(2, "0")}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Coluna de minutos */}
            <div className="flex-1">
              <div className="text-[11px] text-slate-500 text-center font-medium py-1 mb-1 border-b border-slate-100">
                mm
              </div>
              <div className="h-40 overflow-y-auto">
                {minutes.map((m) => {
                  const isSel = selectedM === m;

                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => pickPart(undefined, m, undefined)}
                      className={cn(
                        "block w-full text-sm py-1 rounded text-center transition-colors",
                        isSel
                          ? color === "primary" ? "bg-primary text-contrast-text-primary" : "bg-secondary text-contrast-text-secondary"
                          : "text-slate-700 hover:bg-slate-100"
                      )}
                    >
                      {String(m).padStart(2, "0")}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Coluna AM/PM (apenas em 12h) */}
            {ampm && (
              <div className="w-12 flex flex-col">
                <div className="text-[11px] text-slate-500 text-center font-medium py-1 mb-1 border-b border-slate-100">
                  a
                </div>
                <div className="flex flex-col gap-1">
                  {(["am", "pm"] as const).map((meridiem) => {
                    const isSel = selectedMeridiem === meridiem;

                    return (
                      <button
                        key={meridiem}
                        type="button"
                        onClick={() => pickPart(undefined, undefined, meridiem)}
                        className={cn(
                          "block w-full text-sm py-1 rounded text-center transition-colors uppercase",
                          isSel
                            ? color === "primary" ? "bg-primary text-contrast-text-primary" : "bg-secondary text-contrast-text-secondary"
                            : "text-slate-700 hover:bg-slate-100"
                        )}
                      >
                        {meridiem}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {!autoOk && (
            <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { emitChange(null); setInputValue(""); setOpen(false); }}
                className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1"
              >
                Limpar
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className={cn(
                  "text-xs px-3 py-1 rounded font-semibold",
                  color === "primary" ? "text-primary hover:bg-primary/10" : "text-secondary hover:bg-secondary/10"
                )}
              >
                OK
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

KeyboardTimePicker.displayName = "KeyboardTimePicker";

export default KeyboardTimePicker;
